/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useMemo, useState } from 'react';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import useServiceRequestTypeHierarchy from './useServiceRequestTypeHierarchy';
import { getLeafUid } from '../utils/serviceRequestHierarchy';
import toastService from '../utils/toastService';
import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

let pairSeq = 0;
const newPair = (key = '', value = '') => ({ id: ++pairSeq, key, value });

/** `{ [depth]: uid }` picker value for the path from the root down to `leafUid`. */
const valueFromLeaf = (nodes, leafUid) => {
  const byUid = new Map(nodes.map((n) => [n.uid, n]));
  const value = {};
  let node = byUid.get(leafUid);
  while (node) {
    value[node.depth] = node.uid;
    node = node.parentUid ? byUid.get(node.parentUid) : null;
  }
  return value;
};

/**
 * State of the knowledge base document metadata form shared by the upload and
 * edit pages: the optional service request type with its hierarchy picks, and
 * the custom key/value pairs. `reservedKeys` are rejected as custom keys.
 * `prefill()` loads an existing document (pairs plus the matched type/option).
 */
const useDocumentMetadataForm = (accountId, { reservedKeys = [] } = {}) => {
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeUid, setTypeUid] = useState('');
  const [hierarchyValue, setHierarchyValue] = useState({});
  const [pairs, setPairs] = useState([]);
  const [pairErrors, setPairErrors] = useState({});
  // Leaf option to select once the prefilled type's hierarchy has loaded. State (not a ref) so a
  // repeated prefill — e.g. StrictMode's double load — re-runs the selection effect.
  const [pendingLeaf, setPendingLeaf] = useState(null);

  const { levels, nodes, loading: hierarchyLoading } = useServiceRequestTypeHierarchy(typeUid || undefined);

  useEffect(() => {
    if (!accountId) { return; }
    const fetchTypes = async () => {
      try {
        setTypesLoading(true);
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ accountUid: accountId, limit: 100 });
        setTypes(data.data || []);
      } catch {
        setTypes([]);
        toastService.error(`Failed to load ${SERVICE_REQUEST_LABEL.singularLower} types`);
      } finally {
        setTypesLoading(false);
      }
    };
    fetchTypes();
  }, [accountId]);

  // Select the prefilled path once the leaf is among the loaded options. Checking for the leaf
  // itself (uids are unique) skips renders that still hold the previous type's nodes.
  useEffect(() => {
    if (!pendingLeaf || hierarchyLoading) { return; }
    if (!nodes.some((n) => n.uid === pendingLeaf)) { return; }
    setHierarchyValue(valueFromLeaf(nodes, pendingLeaf));
    setPendingLeaf(null);
  }, [pendingLeaf, nodes, hierarchyLoading]);

  // Metadata added by the server from the hierarchy picks (mirrored here for preview & validation).
  // The service request type itself is not sent — it only drives the hierarchy selection.
  const systemMetadata = useMemo(() => {
    if (!typeUid) { return {}; }
    const metadata = {};
    levels.forEach((level, depth) => {
      const nodeUid = hierarchyValue[depth];
      const node = nodeUid && nodes.find((n) => n.uid === nodeUid);
      if (node) { metadata[level.key] = node.name; }
    });
    return metadata;
  }, [typeUid, levels, nodes, hierarchyValue]);

  const filledPairs = pairs.filter((p) => p.key.trim() || p.value.trim());
  const customMetadata = filledPairs.reduce((acc, p) => {
    if (p.key.trim() && p.value.trim()) { acc[p.key.trim()] = p.value.trim(); }
    return acc;
  }, {});
  const previewMetadata = { ...customMetadata, ...systemMetadata };

  /** Validates the pairs, shows the errors and returns true when there are none. */
  const validate = () => {
    const errors = {};
    const seen = new Set();
    filledPairs.forEach((p) => {
      const key = p.key.trim();
      if (!key) {
        errors[p.id] = 'Key is required';
      } else if (!p.value.trim()) {
        errors[p.id] = 'Value is required';
      } else if (seen.has(key)) {
        errors[p.id] = `Duplicate key "${key}"`;
      } else if (reservedKeys.includes(key)) {
        errors[p.id] = `"${key}" is a system key and cannot be set`;
      } else if (Object.prototype.hasOwnProperty.call(systemMetadata, key)) {
        errors[p.id] = `"${key}" is already set by the selected hierarchy`;
      }
      seen.add(key);
    });
    setPairErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const changeType = (uid) => {
    setPendingLeaf(null);
    setTypeUid(uid);
    setHierarchyValue({});
  };

  const addPair = () => setPairs((prev) => [...prev, newPair()]);

  const updatePair = (pairId, field, value) => {
    setPairs((prev) => prev.map((p) => (p.id === pairId ? { ...p, [field]: value } : p)));
    setPairErrors((prev) => ({ ...prev, [pairId]: null }));
  };

  const removePair = (pairId) => {
    setPairs((prev) => prev.filter((p) => p.id !== pairId));
  };

  const prefill = ({ metadata = {}, serviceRequestTypeUid = '', hierarchyNodeUid = null } = {}) => {
    setPairs(Object.entries(metadata).map(([key, value]) => newPair(key, String(value))));
    setPairErrors({});
    setPendingLeaf(serviceRequestTypeUid && hierarchyNodeUid ? hierarchyNodeUid : null);
    setTypeUid(serviceRequestTypeUid || '');
    setHierarchyValue({});
  };

  const leafUid = typeUid ? getLeafUid(hierarchyValue) : null;

  return {
    types,
    typesLoading,
    typeUid,
    changeType,
    levels,
    nodes,
    hierarchyLoading,
    hierarchyValue,
    setHierarchyValue,
    pairs,
    pairErrors,
    addPair,
    updatePair,
    removePair,
    customMetadata,
    previewMetadata,
    validate,
    prefill,
    leafUid,
  };
};

export default useDocumentMetadataForm;
